import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  CreditCard,
  BookOpen,
  Wallet as WalletIcon,
  Package,
  Info,
  UserSearch,
  Split,
} from 'lucide-react';
import { PageGuard } from '@components/PageGuard';
import { OwnerPicker } from '@components/finance/OwnerPicker';
import { ledgerService, type TraceJournalEntry } from '@services/ledger.service';
import { ordersService } from '@services/orders.service';
import type { OwnerSearchResult } from '@services/search.service';
import { cn, formatCurrency } from '@lib/utils';
import { chipTxnType, formatTimestamp, labelTxnType, shortId } from './ledgerFormat';

const inputClass =
  'rounded-2xl border border-[var(--border-default)] bg-[var(--bg-surface)] px-4 py-3 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors';

const thClass =
  'text-left px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] whitespace-nowrap';

const tdClass = 'px-6 py-3 text-[14px] text-[var(--text-primary)] whitespace-nowrap';

const sectionCard = 'rounded-3xl bg-[var(--bg-surface)] shadow-soft overflow-hidden';

function errorStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status?: unknown }).status;
    return typeof status === 'number' ? status : undefined;
  }
  return undefined;
}

/** One journal entry rendered as a real double-entry block. */
const JournalEntryBlock = ({ entry }: { entry: TraceJournalEntry }) => (
  <div
    className={cn(
      'rounded-3xl bg-[var(--bg-surface)] shadow-soft overflow-hidden',
      !entry.balanced && 'border border-[var(--state-danger-border)]',
    )}
  >
    <div className="px-6 py-5 border-b border-[var(--border-subtle)] flex flex-wrap items-start justify-between gap-3">
      <div className="space-y-1 min-w-0">
        <h3 className="text-[15px] font-semibold text-[var(--text-primary)]">
          {entry.description ?? 'Journal entry'}
        </h3>
        <p className="text-[12px] text-[var(--text-muted)]">
          Entry {shortId(entry.id)} · {entry.status} · posted {formatTimestamp(entry.postedAt)}
        </p>
      </div>
      <span
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold shrink-0',
          entry.balanced
            ? 'bg-[var(--state-success-bg)] text-[var(--state-success)]'
            : 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
        )}
      >
        {entry.balanced ? (
          <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} />
        ) : (
          <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.5} />
        )}
        {entry.balanced ? 'Balanced' : 'Unbalanced'}
      </span>
    </div>

    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-[var(--bg-base)]">
          <tr>
            <th className={thClass}>Ledger account</th>
            <th className={thClass}>Wallet</th>
            <th className={thClass}>Debit</th>
            <th className={thClass}>Credit</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--border-subtle)]">
          {entry.lines.map((line) => (
            <tr key={line.id}>
              <td className="px-6 py-3">
                <div className="text-[14px] font-medium text-[var(--text-primary)]">
                  {line.accountCode} · {line.accountName}
                </div>
                <div className="text-[12px] text-[var(--text-muted)]">
                  {line.accountType}
                  {line.description ? ` · ${line.description}` : ''}
                </div>
              </td>
              <td className="px-6 py-3 text-[13px]">
                {line.walletId ? (
                  <Link
                    to={`/finance/wallets/${line.walletId}`}
                    className="text-[var(--text-accent)] hover:underline"
                  >
                    {line.walletLabel ?? shortId(line.walletId)}
                  </Link>
                ) : (
                  <span className="text-[var(--text-muted)]">—</span>
                )}
              </td>
              <td className={tdClass}>
                {line.debitAmount ? formatCurrency(line.debitAmount) : '—'}
              </td>
              <td className={tdClass}>
                {line.creditAmount ? formatCurrency(line.creditAmount) : '—'}
              </td>
            </tr>
          ))}

          {entry.lines.length === 0 && (
            <tr>
              <td colSpan={4} className="px-6 py-8 text-center text-[14px] text-[var(--text-muted)]">
                This entry has no lines — nothing was actually posted.
              </td>
            </tr>
          )}
        </tbody>
        <tfoot className="bg-[var(--bg-base)]">
          <tr>
            <td className="px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
              Totals
            </td>
            <td />
            <td
              className={cn(
                'px-6 py-3 text-[14px] font-semibold',
                entry.balanced ? 'text-[var(--text-primary)]' : 'text-[var(--state-danger)]',
              )}
            >
              {formatCurrency(entry.debitTotal)}
            </td>
            <td
              className={cn(
                'px-6 py-3 text-[14px] font-semibold',
                entry.balanced ? 'text-[var(--text-primary)]' : 'text-[var(--state-danger)]',
              )}
            >
              {formatCurrency(entry.creditTotal)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
);

const CUSTOMER_ORDER_LIMIT = 15;

export const MoneyTracePage = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [input, setInput] = useState(orderId ?? '');
  const [customer, setCustomer] = useState<OwnerSearchResult | null>(null);

  useEffect(() => {
    setInput(orderId ?? '');
  }, [orderId]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['ledger', 'money-trace', orderId],
    queryFn: () => ledgerService.getOrderMoneyTrace(orderId!),
    enabled: !!orderId,
    retry: false,
  });

  // Reaching a trace with no id in hand: pick the person, then their order.
  const {
    data: customerOrders,
    isLoading: ordersLoading,
    error: ordersError,
  } = useQuery({
    queryKey: ['admin', 'orders', 'by-customer', customer?.ownerId],
    queryFn: () =>
      ordersService.list({ customerId: customer!.ownerId, limit: CUSTOMER_ORDER_LIMIT }),
    enabled: !!customer,
    retry: false,
  });

  // Defensive: the admin list has historically come back both enveloped and bare.
  const customerOrderRows = Array.isArray(customerOrders?.data) ? customerOrders.data : [];

  const notFound = errorStatus(error) === 404;
  const fromBasket = data?.resolvedFrom === 'basket';
  const focusBasketId = data?.focusBasketId ?? null;

  return (
    <PageGuard permission="wallets.read">
      <div className="space-y-8">
        {/* Header */}
        <div className="space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
            Finance
          </p>
          <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
            Money trace
          </h1>
          <p className="text-[14px] text-[var(--text-muted)]">
            How every coin of one order entered the platform and where it sits now —
            payment in, journal entries posted, wallets moved.
          </p>
        </div>

        {/* Order or basket id input */}
        <div className="space-y-2">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const trimmed = input.trim();
              if (trimmed) navigate(`/finance/trace/${trimmed}`);
            }}
            className="flex flex-wrap items-center gap-3"
          >
            <div className="relative flex-1 min-w-[280px] max-w-[560px]">
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]"
                strokeWidth={1.5}
              />
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Paste an order id or a basket id..."
                className={cn(inputClass, 'w-full pl-10')}
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-6 py-3 text-[14px] font-medium text-white hover:opacity-90 transition-opacity"
            >
              Trace
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
            {orderId && (
              <Link
                to={`/finance/splits/${orderId}`}
                className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] px-5 py-3 text-[14px] font-medium text-[var(--text-primary)] transition-colors hover:border-[var(--border-strong)]"
              >
                <Split className="w-4 h-4" strokeWidth={1.5} />
                Split this order
              </Link>
            )}
          </form>
          <p className="text-[12px] text-[var(--text-muted)]">
            Either works. A per-merchant basket id resolves up to its parent order —
            money is only coherent at order level.
          </p>
        </div>

        {/* No id in hand: find the customer, then their order */}
        <section className="space-y-4 rounded-3xl bg-[var(--bg-surface)] p-6 shadow-soft">
          <div className="flex items-start gap-2">
            <UserSearch
              className="mt-0.5 h-5 w-5 shrink-0 text-[var(--text-muted)]"
              strokeWidth={1.5}
            />
            <div className="space-y-1">
              <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
                No id? Start from the customer
              </h2>
              <p className="text-[13px] text-[var(--text-muted)]">
                Search the person who complained, confirm it is them by phone or email,
                then open the order in question.
              </p>
            </div>
          </div>

          <div className="max-w-[560px]">
            <OwnerPicker
              label="Customer"
              ownerType="user"
              value={customer}
              onChange={setCustomer}
              placeholder="Search by name, phone or email..."
            />
          </div>

          {customer && (
            <div className="overflow-hidden rounded-2xl border border-[var(--border-subtle)]">
              {ordersLoading && (
                <p className="px-5 py-8 text-center text-[13px] text-[var(--text-muted)]">
                  Loading {customer.label}'s orders...
                </p>
              )}

              {!!ordersError && !ordersLoading && (
                <p className="px-5 py-8 text-center text-[13px] text-[var(--state-danger)]">
                  Could not load orders for this customer.
                </p>
              )}

              {customerOrders && !ordersLoading && (
                <table className="w-full">
                  <thead className="bg-[var(--bg-base)]">
                    <tr>
                      <th className={thClass}>Placed</th>
                      <th className={thClass}>Order</th>
                      <th className={thClass}>Status</th>
                      <th className={thClass}>Payment</th>
                      <th className={thClass}>Total</th>
                      <th className={thClass} />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border-subtle)]">
                    {customerOrderRows.map((o) => (
                      <tr key={o.id} className="hover:bg-[var(--bg-base)] transition-colors">
                        <td className={cn(tdClass, 'text-[var(--text-muted)]')}>
                          {formatTimestamp(o.createdAt)}
                        </td>
                        <td className={cn(tdClass, 'font-mono text-[13px] text-[var(--text-muted)]')}>
                          {shortId(o.id)}
                        </td>
                        <td className={cn(tdClass, 'capitalize text-[var(--text-secondary)]')}>
                          {String(o.status).replace(/_/g, ' ')}
                        </td>
                        <td className={cn(tdClass, 'capitalize text-[var(--text-secondary)]')}>
                          {(o.paymentStatus ?? '—').replace(/_/g, ' ')}
                        </td>
                        <td className={cn(tdClass, 'font-medium')}>
                          {formatCurrency(o.totalAmount)}
                        </td>
                        <td className="px-6 py-3">
                          <button
                            type="button"
                            onClick={() => navigate(`/finance/trace/${o.id}`)}
                            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-[var(--border-default)] px-4 py-1.5 text-[13px] font-medium text-[var(--text-primary)] transition-colors hover:border-[var(--border-strong)]"
                          >
                            Trace
                            <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                          </button>
                        </td>
                      </tr>
                    ))}

                    {customerOrderRows.length === 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-6 py-10 text-center text-[14px] text-[var(--text-muted)]"
                        >
                          {customer.label} has no orders.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </section>

        {!orderId && (
          <div className={cn(sectionCard, 'p-12 text-center text-[14px] text-[var(--text-muted)]')}>
            Enter an order or basket id above — or pick a customer and choose one of
            their orders — to trace the money.
          </div>
        )}

        {orderId && isLoading && (
          <div className={cn(sectionCard, 'p-12 text-center text-[14px] text-[var(--text-muted)]')}>
            Tracing order {shortId(orderId)}...
          </div>
        )}

        {orderId && !!error && !isLoading && (
          <div className={cn(sectionCard, 'p-12 text-center space-y-2')}>
            <p className="text-[14px] text-[var(--state-danger)]">
              {notFound
                ? 'No order or basket found with that id.'
                : 'Failed to trace this order.'}
            </p>
            {notFound && (
              <p className="text-[13px] text-[var(--text-muted)]">
                Both an order id and a per-merchant basket id resolve here. If neither
                matched, find the customer above and pick the order from their list.
              </p>
            )}
          </div>
        )}

        {data && (
          <>
            {/* Searched a basket — say so, loudly, before anything is read */}
            {fromBasket && (
              <div className="flex items-start gap-3 rounded-3xl border border-[var(--border-strong)] bg-[var(--bg-elevated)] p-6">
                <Info
                  className="mt-0.5 h-5 w-5 shrink-0 text-[var(--text-accent)]"
                  strokeWidth={1.5}
                />
                <div className="space-y-1">
                  <p className="text-[14px] font-semibold text-[var(--text-primary)]">
                    You searched a basket — showing its parent order
                  </p>
                  <p className="text-[13px] text-[var(--text-secondary)]">
                    Basket {shortId(focusBasketId)} belongs to order {shortId(data.order.id)}.
                    The payment, journal entries and wallet movements below cover the
                    whole order; the basket you searched is highlighted in section 4.
                  </p>
                </div>
              </div>
            )}

            {/* Order summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="rounded-3xl bg-[var(--bg-surface)] shadow-soft p-6 md:col-span-2">
                <div className="text-[13px] text-[var(--text-muted)]">Order total</div>
                <div className="font-display text-[32px] font-medium tracking-[-0.03em] text-[var(--text-primary)] mt-2 leading-none">
                  {formatCurrency(data.order.totalAmount)}
                </div>
                <p className="text-[12px] text-[var(--text-muted)] mt-3 font-mono break-all">
                  {data.order.id}
                </p>
              </div>
              <div className="rounded-3xl bg-[var(--bg-surface)] shadow-soft p-6">
                <div className="text-[13px] text-[var(--text-muted)]">Order status</div>
                <div className="text-[18px] font-semibold text-[var(--text-primary)] mt-2 capitalize">
                  {data.order.status.replace(/_/g, ' ')}
                </div>
                <p className="text-[12px] text-[var(--text-muted)] mt-3">
                  Placed {formatTimestamp(data.order.createdAt)}
                </p>
              </div>
              <div className="rounded-3xl bg-[var(--bg-surface)] shadow-soft p-6">
                <div className="text-[13px] text-[var(--text-muted)]">Payment status</div>
                <div className="text-[18px] font-semibold text-[var(--text-primary)] mt-2 capitalize">
                  {data.order.paymentStatus.replace(/_/g, ' ')}
                </div>
                <p className="text-[12px] text-[var(--text-muted)] mt-3">
                  {data.order.customerId ? (
                    <Link
                      to={`/finance/owners/user/${data.order.customerId}`}
                      className="text-[var(--text-accent)] hover:underline"
                    >
                      Open this customer's ledger
                    </Link>
                  ) : (
                    'No customer on this order'
                  )}
                </p>
              </div>
            </div>

            {/* 1. How money entered */}
            <section className="space-y-4">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[var(--text-muted)]" strokeWidth={1.5} />
                <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
                  1 · How the money entered
                </h2>
              </div>
              <p className="text-[13px] text-[var(--text-muted)]">
                Inbound payment attempts on this order. A completed payment is the coin
                arriving; everything after this is the platform moving it around.
              </p>

              <div className={sectionCard}>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[var(--bg-base)]">
                      <tr>
                        <th className={thClass}>When</th>
                        <th className={thClass}>Amount</th>
                        <th className={thClass}>Net</th>
                        <th className={thClass}>Status</th>
                        <th className={thClass}>Direction</th>
                        <th className={thClass}>External reference</th>
                        <th className={thClass}>Completed</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--border-subtle)]">
                      {data.paymentTransactions.map((p) => (
                        <tr key={p.id} className="hover:bg-[var(--bg-base)] transition-colors">
                          <td className={cn(tdClass, 'text-[var(--text-muted)]')}>
                            {formatTimestamp(p.createdAt)}
                          </td>
                          <td className={cn(tdClass, 'font-medium')}>{formatCurrency(p.amount)}</td>
                          <td className={tdClass}>{formatCurrency(p.netAmount)}</td>
                          <td className="px-6 py-3">
                            <span
                              className={cn(
                                'rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize',
                                p.status === 'completed' || p.status === 'success'
                                  ? 'bg-[var(--state-success-bg)] text-[var(--state-success)]'
                                  : p.status === 'failed'
                                    ? 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]'
                                    : 'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
                              )}
                            >
                              {p.status}
                            </span>
                          </td>
                          <td className={cn(tdClass, 'text-[var(--text-muted)]')}>
                            {p.direction ?? '—'}
                          </td>
                          <td className="px-6 py-3 text-[13px] text-[var(--text-secondary)]">
                            <span className="block max-w-[240px] truncate font-mono">
                              {p.externalTransactionId ?? p.mpesaCheckoutRequestId ?? '—'}
                            </span>
                            {p.failureReason && (
                              <span className="block text-[12px] text-[var(--state-danger)]">
                                {p.failureReason}
                              </span>
                            )}
                          </td>
                          <td className={cn(tdClass, 'text-[var(--text-muted)]')}>
                            {formatTimestamp(p.completedAt)}
                          </td>
                        </tr>
                      ))}

                      {data.paymentTransactions.length === 0 && (
                        <tr>
                          <td
                            colSpan={7}
                            className="px-6 py-12 text-center text-[14px] text-[var(--text-muted)]"
                          >
                            No payment transactions recorded — no money entered for this
                            order.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* 2. Journal entries */}
            <section className="space-y-4">
              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[var(--text-muted)]" strokeWidth={1.5} />
                <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
                  2 · What the ledger recorded
                </h2>
              </div>
              <p className="text-[13px] text-[var(--text-muted)]">
                Every journal entry posted against this order and its baskets, in the
                order it was posted. Debits must equal credits in each block.
              </p>

              <div className="space-y-6">
                {data.journalEntries.map((entry) => (
                  <JournalEntryBlock key={entry.id} entry={entry} />
                ))}

                {data.journalEntries.length === 0 && (
                  <div
                    className={cn(sectionCard, 'p-12 text-center text-[14px] text-[var(--text-muted)]')}
                  >
                    No journal entries posted for this order.
                  </div>
                )}
              </div>
            </section>

            {/* 3. Wallet movements */}
            <section className="space-y-4">
              <div className="flex items-center gap-2">
                <WalletIcon className="w-5 h-5 text-[var(--text-muted)]" strokeWidth={1.5} />
                <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
                  3 · Where the money moved
                </h2>
              </div>
              <p className="text-[13px] text-[var(--text-muted)]">
                Each wallet touched by this order, with the counterparty on the other
                side of the move and the balance that wallet was left holding.
              </p>

              <div className={sectionCard}>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[var(--bg-base)]">
                      <tr>
                        <th className={thClass}>When</th>
                        <th className={thClass}>Type</th>
                        <th className={thClass}>Movement</th>
                        <th className={thClass}>Amount</th>
                        <th className={thClass}>Balance after</th>
                        <th className={thClass}>Description</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--border-subtle)]">
                      {data.walletMovements.map((m) => (
                        <tr key={m.id} className="hover:bg-[var(--bg-base)] transition-colors">
                          <td className={cn(tdClass, 'text-[var(--text-muted)]')}>
                            {formatTimestamp(m.createdAt)}
                          </td>
                          <td className="px-6 py-3">
                            <span
                              className={cn(
                                'rounded-full px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap',
                                chipTxnType(m.transactionType),
                              )}
                            >
                              {labelTxnType(m.transactionType)}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-[13px]">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Link
                                to={`/finance/wallets/${m.walletId}`}
                                className="text-[var(--text-accent)] hover:underline"
                              >
                                {m.walletLabel ?? shortId(m.walletId)}
                              </Link>
                              {m.counterpartyWalletId && (
                                <>
                                  <ArrowRight
                                    className="w-3.5 h-3.5 text-[var(--text-muted)]"
                                    strokeWidth={1.5}
                                  />
                                  <Link
                                    to={`/finance/wallets/${m.counterpartyWalletId}`}
                                    className="text-[var(--text-accent)] hover:underline"
                                  >
                                    {m.counterpartyLabel ?? shortId(m.counterpartyWalletId)}
                                  </Link>
                                </>
                              )}
                            </div>
                          </td>
                          <td className={cn(tdClass, 'font-medium')}>{formatCurrency(m.amount)}</td>
                          <td className={tdClass}>{formatCurrency(m.runningBalance)}</td>
                          <td className="px-6 py-3 text-[13px] text-[var(--text-secondary)]">
                            <span className="block max-w-[280px] truncate">
                              {m.description ?? '—'}
                            </span>
                          </td>
                        </tr>
                      ))}

                      {data.walletMovements.length === 0 && (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-6 py-12 text-center text-[14px] text-[var(--text-muted)]"
                          >
                            No wallet movements recorded for this order.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* 4. Baskets */}
            <section className="space-y-4">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-[var(--text-muted)]" strokeWidth={1.5} />
                <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
                  4 · Baskets in this order
                </h2>
              </div>
              <p className="text-[13px] text-[var(--text-muted)]">
                One basket per merchant. Escrow releases per basket when the buyer
                confirms it, so basket status explains what is still held.
              </p>

              <div className={sectionCard}>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[var(--bg-base)]">
                      <tr>
                        <th className={thClass}>Basket</th>
                        <th className={thClass}>Business</th>
                        <th className={thClass}>Status</th>
                        <th className={thClass}>Amount</th>
                        <th className={thClass}>Confirmed</th>
                        <th className={thClass} />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--border-subtle)]">
                      {data.baskets.map((b) => {
                        const focused = !!focusBasketId && b.id === focusBasketId;
                        return (
                        <tr
                          key={b.id}
                          className={cn(
                            'transition-colors',
                            focused
                              ? 'bg-[var(--state-success-bg)]'
                              : 'hover:bg-[var(--bg-base)]',
                          )}
                        >
                          <td className={cn(tdClass, 'font-mono text-[13px] text-[var(--text-muted)]')}>
                            <span className="flex items-center gap-2">
                              {shortId(b.id)}
                              {focused && (
                                <span className="rounded-full bg-[var(--state-success-bg)] px-2 py-0.5 font-sans text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--state-success)]">
                                  You searched this
                                </span>
                              )}
                            </span>
                          </td>
                          <td className={tdClass}>{b.businessName ?? '—'}</td>
                          <td className={cn(tdClass, 'capitalize text-[var(--text-secondary)]')}>
                            {b.status.replace(/_/g, ' ')}
                          </td>
                          <td className={cn(tdClass, 'font-medium')}>
                            {formatCurrency(b.totalAmount)}
                          </td>
                          <td className={cn(tdClass, 'text-[var(--text-muted)]')}>
                            {formatTimestamp(b.confirmedAt)}
                          </td>
                          <td className="px-6 py-3">
                            <Link
                              to={`/finance/splits/${b.id}`}
                              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-[var(--border-default)] px-4 py-1.5 text-[13px] font-medium text-[var(--text-primary)] transition-colors hover:border-[var(--border-strong)]"
                            >
                              Split
                              <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                            </Link>
                          </td>
                        </tr>
                        );
                      })}

                      {data.baskets.length === 0 && (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-6 py-12 text-center text-[14px] text-[var(--text-muted)]"
                          >
                            No baskets on this order.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </PageGuard>
  );
};
